import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, SERVICES } from "../../lib/api";
import { inputCls } from "./AdminHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";

const EMPTY = { name: "", phone: "", email: "", vehicle: "", reg: "", service: "Starlights", package: "", package_price: "", notes: "" };

export default function ManualBookingDialog({ open, onOpenChange, date, block, onError, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [packages, setPackages] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data.packages || {})).catch(() => {});
  }, []);

  useEffect(() => { if (open) setForm(EMPTY); }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pkgOptions = packages[form.service] || [];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/bookings", {
        ...form,
        email: form.email.trim() || null,
        package: form.package || null,
        package_price: form.package_price === "" ? null : Number(form.package_price),
        booking_date: date,
        time_slot: block.slot,
      });
      toast.success(`${form.name} booked in`);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg" data-testid="manual-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Book a customer in</DialogTitle>
          <p className="text-sm text-zinc-400">{date} · {block?.label}</p>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Field label="Name *" className="col-span-2"><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="mb-name" /></Field>
          <Field label="Phone"><input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="mb-phone" /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="mb-email" /></Field>
          <Field label="Car"><input value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. Mercedes A-Class" className={inputCls} data-testid="mb-vehicle" /></Field>
          <Field label="Reg"><input value={form.reg} onChange={set("reg")} className={`${inputCls} uppercase`} data-testid="mb-reg" /></Field>
          <Field label="Service">
            <select value={form.service} onChange={(e) => setForm((f) => ({ ...f, service: e.target.value, package: "" }))} className={inputCls} data-testid="mb-service">
              {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Package">
            {pkgOptions.length ? (
              <select value={form.package} onChange={set("package")} className={inputCls} data-testid="mb-package">
                <option value="">—</option>
                {pkgOptions.map((p) => <option key={p.name} value={p.name}>{p.name} · £{p.price}</option>)}
              </select>
            ) : (
              <input value={form.package} onChange={set("package")} placeholder="Describe the job" className={inputCls} data-testid="mb-package" />
            )}
          </Field>
          <Field label="Price £ (optional)"><input type="number" min="0" step="1" value={form.package_price} onChange={set("package_price")} placeholder="Package price" className={inputCls} data-testid="mb-price" /></Field>
          <Field label="Notes" className="col-span-2"><textarea rows={2} value={form.notes} onChange={set("notes")} className={inputCls} data-testid="mb-notes" /></Field>
          <button type="submit" disabled={saving} data-testid="mb-submit" className="col-span-2 mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#D946EF] text-white font-semibold disabled:opacity-40 hover:bg-[#F0ABFC] transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save booking
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-zinc-400 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
